import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { WorkflowStage } from '~/types/predict';
import NinetyDaySummaryShell from '~/components/patient-board/NinetyDaySummaryShell';
import PatientBoardHeader from '~/components/patient-board/PatientBoardHeader';
import PatientPhaseAccordion from '~/components/patient-board/PatientPhaseAccordion';
import NinetyDaySummary from '~/components/predict/patient-detail/NinetyDaySummary';
import ProgramFitFactorsList from '~/components/predict/patient-detail/ProgramFitFactorsList';
import QualifyStageContent from '~/components/predict/patient-detail/QualifyStageContent';
import TransferStageContent from '~/components/predict/patient-detail/TransferStageContent';
import { mapPredictPatient, makePredictConfig, PREDICT_PHASES, stageToPhase } from './predictToVM';

/**
 * Predict Patient Detail — shares the Coordinate (discharge) detail layout
 * (header + 90-day summary + phase accordion). Phases are Predict / Qualify /
 * Transfer; there is no urgency pill. Stage content: Predict = Program Fit &
 * Factors, Qualify = Capacity, Transfer = read-only transfer-order status.
 */
const PredictPatientDetail: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { getPatientById, initializeChecklist, markPatientViewed, takeOwnership } = usePredictContext();

  const patient = patientId ? getPatientById(patientId) : undefined;
  const config = useMemo(() => makePredictConfig(takeOwnership), [takeOwnership]);

  // Scroll to top when navigating to a patient detail page.
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, [patientId]);

  // Clear the NEW badge once the user opens the patient detail view.
  useEffect(() => {
    if (patientId) {
      markPatientViewed(patientId);
    }
  }, [patientId]);

  // Initialize checklist when entering ELIGIBILITY stage.
  useEffect(() => {
    if (patient && patient.currentStage === WorkflowStage.ELIGIBILITY && !patient.eligibilityChecklist) {
      initializeChecklist(patient.id);
    }
  }, [patient?.currentStage, patient?.eligibilityChecklist, patient?.id]);

  if (!patient) {
    return (
      <Box sx={{ mt: 1.5, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/predict/all-patients')} sx={{ mb: 2 }}>
          Back to patients
        </Button>
        <Typography color="text.secondary">Patient not found.</Typography>
      </Box>
    );
  }

  const vm = mapPredictPatient(patient);

  return (
    <Box sx={{ mt: 1.5, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/predict/all-patients')} sx={{ mb: 2 }}>
        Back to patients
      </Button>

      <PatientBoardHeader patient={vm} config={config} />

      <NinetyDaySummaryShell>
        {patient.ninetyDaySummary ? (
          <NinetyDaySummary summary={patient.ninetyDaySummary} />
        ) : (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Clinical summary data for the past 90 days is not available for this patient.
            </Typography>
          </Paper>
        )}
      </NinetyDaySummaryShell>

      <PatientPhaseAccordion
        phases={PREDICT_PHASES}
        currentPhase={stageToPhase(patient.currentStage)}
        syncKey={`${patient.currentStage}-${patient.selectedProgram ?? ''}`}
        content={{
          predict: <ProgramFitFactorsList patient={patient} />,
          qualify: <QualifyStageContent patient={patient} />,
          transfer: <TransferStageContent patient={patient} />,
        }}
      />
    </Box>
  );
};

export default PredictPatientDetail;
